<template>
  <button
    :class="[
      'button',
      {
        action,
        off,
        w100p,
        w40p,
        w25p,
        variant1,
        variant2,
        variant3,
        variant4,
        variant5,
        variant6,
        red,
        hover,
        'full-black': fullBlack,
        'full-red': fullRed,
        'button--loading': spin,
        'payment-done': paymentDone,
      },
    ]"
    :style="inlineStyle"
    v-bind="$attrs"
    type="button"
    :disabled="disabled || spin"
  >
    <div :class="['btn-text', { variant2 }]">
      <slot
        v-if="icon"
        name="icon"
      >
        <v-icon
          :name="typeof icon === 'string' ? icon : 'plus-btn'"
          width="20"
          height="20"
          :stroke="
            off
              ? 'var(--color-gray-medium)'
              : disabled
                ? 'var(--color-gray-medium)'
                : action || variant1
                  ? 'var(--color-white)'
                  : hover
                    ? 'var(--color-primary-dark)cc'
                    : red
                      ? '#fe4a49'
                      : fullBlack
                        ? 'var(--color-white)'
                        : 'var(--color-accent)'
          "
        />
      </slot>
      <slot />
    </div>
  </button>
</template>

<script setup>
defineProps({
  action: {
    type: Boolean,
    default: () => false,
  },
  icon: {
    type: [Boolean, String],
    default: () => false,
  },
  spin: {
    type: Boolean,
    default: () => false,
  },
  off: {
    type: Boolean,
    default: () => false,
  },
  fullBlack: {
    type: Boolean,
    default: () => false,
  },
  red: {
    type: Boolean,
    default: () => false,
  },
  fullRed: {
    type: Boolean,
    default: () => false,
  },
  w100p: {
    type: Boolean,
    default: () => false,
  },
  w40p: {
    type: Boolean,
    default: () => false,
  },
  w25p: {
    type: Boolean,
    default: () => false,
  },
  inlineStyle: {
    type: Object,
    default: () => ({}),
  },
  variant1: {
    type: Boolean,
    default: false,
  },
  variant2: {
    type: Boolean,
    default: false,
  },
  variant3: {
    type: Boolean,
    default: false,
  },
  variant4: {
    type: Boolean,
    default: false,
  },
  variant5: {
    type: Boolean,
    default: false,
  },
  variant6: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hover: {
    type: Boolean,
    default: false,
  },
  paymentDone: {
    type: Boolean,
    default: false,
  },
});
</script>

<style lang="scss" scoped>
@keyframes button-loading-spinner {
  from {
    transform: rotate(0turn);
  }

  to {
    transform: rotate(1turn);
  }
}

button {
  display: flex;
  align-items: center;
  justify-content: center;

  &.button--loading {
    & .btn-text {
      visibility: hidden;
      opacity: 0;
    }

    &::after {
      position: absolute;
      content: '';
      width: 20px;
      height: 20px;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      margin: auto;
      border: 3px solid transparent;
      border-top-color: var(--color-accent);
      border-bottom-color: var(--color-accent);
      border-radius: 50%;
      animation: button-loading-spinner 1s ease infinite;
    }
  }

  &.button {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  position: relative;
  padding: 12px 16px;
  text-align: center;
  border-radius: 8px;
  border: 2px solid var(--color-accent);
  background: var(--color-white);
  /* outline: var(--color-accent); */
  color: var(--color-accent);
  cursor: pointer;
  transition: all 0.4s;

  &:active:enabled:not(:disabled) {
    color: var(--color-white);
    background: var(--color-accent-active);
    border: 2px solid transparent;
    outline: 2px solid transparent;

    & .btn-text {
      filter: brightness(0) saturate(100%) invert(100%) sepia(25%) saturate(10%) hue-rotate(158deg) brightness(102%)
        contrast(104%);
    }
  }

  &:not(:disabled):hover {
    color: var(--color-white);
    background: var(--color-accent-hover);
    border: 2px solid transparent;

    & .btn-text {
      filter: brightness(0) saturate(100%) invert(100%) sepia(25%) saturate(10%) hue-rotate(158deg) brightness(102%)
        contrast(104%);
    }
  }
}

.action {
  background: var(--color-accent);
  color: var(--color-white);
  height: 100%;

  &:active:enabled {
    background: var(--color-accent-active);
    border: 2px solid transparent;
  }

  &:not(:disabled):hover {
    background: var(--color-accent-hover);
    border: 2px solid transparent;
  }
}

.w100p {
  width: 100%;
}

.w40p {
  width: 40%;
}

.w25p {
  width: 25%;
}

.off,
[disabled] {
  background: #eeeff2;
  color: var(--color-gray-medium);
  border: none !important;
  outline: none;
  cursor: not-allowed;
}

.btn-text {
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 8px;
  white-space: nowrap;
  font-style: normal;
  user-select: none;
  font:
    500 16px/130% 'Inter',
    sans-serif;

  button:not(:disabled) &:hover {
    filter: brightness(0) saturate(100%) invert(100%) sepia(25%) saturate(10%) hue-rotate(158deg) brightness(102%)
      contrast(104%);
  }
}

.variant1 {
  padding: 8px 20px;
  background: #6ca1f8;
  color: var(--color-white);
  border-color: #6ca1f8;

  &:hover:enabled,
  &:not(:disabled):hover {
    background: #3b82f6;
    border-color: #3b82f6;
    outline: 2px solid transparent;
  }

  &:active:enabled,
  &:active:enabled:not(:disabled) {
    background: #2273f6;
    border-color: #2273f6;
  }
}


.payment-done {
  height: 45px;
  box-sizing: border-box;
  border: 2px solid #00eebb;
  outline: 2px solid transparent;
  text-align: left;
  font:
    500 16px/130% 'Inter',
    sans-serif;
  background: #00eebb;
  color: var(--color-white);

  &:disabled {
    background: #ebeaea;
    color: var(--color-gray-medium);
  }
}

.full-black {
  height: 45px;
  box-sizing: border-box;
  border: 2px solid var(--color-primary-dark);
  outline: 2px solid transparent;
  background: $primary-color-dark;
  outline: var(--color-primary-dark);
  text-align: left;
  font:
    500 16px/130% 'Inter',
    sans-serif;
  color: var(--color-white);

  &:hover:not(:disabled) {
    background: #202033 !important;
    box-shadow: 0px 4px 16px 0px rgba(14, 14, 34, 0.04) !important;
    color: var(--color-white) !important;
    border: 2px solid transparent;
    outline: 2px solid transparent;
  }

  &:active:not(:disabled) {
    background: linear-gradient(302deg, var(--color-primary-dark) 14.21%, #3c3c4c 111.51%) !important;
    color: var(--color-white) !important;
  }

  &:disabled {
    background: var(--color-gray-medium);
  }

  &:disabled:hover {
    background: var(--color-gray-medium);
  }
}

.red {
  box-sizing: border-box;
  border: 2px solid #fe4a49;
  /* outline: 2px solid #fe4a49; */
  background: transparent;
  text-align: left;
  font:
    500 16px/130% 'Inter',
    sans-serif;
  color: #fe4a49;

  &:hover:not(:disabled) {
    background: #fe4a49 !important;
    box-shadow: 0px 4px 16px 0px rgba(14, 14, 34, 0.04) !important;
    color: var(--color-white) !important;
    border: 2px solid transparent;
    /* outline: 2px solid transparent; */
  }

  &:active:not(:disabled) {
    background: linear-gradient(302deg, #fe4a49 14.21%, #d96060 111.51%) !important;
    color: var(--color-white) !important;
  }

  &:disabled {
    background: #ebeaea;
    color: var(--color-gray-medium);
    border: 2px solid transparent !important;
    /* outline: 2px solid transparent; */
  }
}

.full-red {
  box-sizing: border-box;
  border: 2px solid #fe4a49;
  outline: 2px solid transparent;
  background: #fe4a49;
  outline: #fe4a49;
  text-align: left;
  font:
    500 16px/130% 'Inter',
    sans-serif;
  color: var(--color-white);

  &:hover {
    background: #fe4a49 !important;
    box-shadow: 0px 4px 16px 0px rgba(14, 14, 34, 0.04) !important;
    color: var(--color-white) !important;
    border: 2px solid transparent;
    outline: 2px solid transparent;
  }

  &:active {
    background: linear-gradient(302deg, #fe4a49 14.21%, #d96060 111.51%) !important;
    color: var(--color-white) !important;
  }
}

.variant2 {
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%;
}

.variant5 {
  padding: 12px 20px;
  border-radius: 8px;
  padding: 13px 20px;
  max-width: 65px;
  flex-basis: 65px;
}

.variant6 {
  padding: 16px;
}
</style>
